/**
 * 기능 ID : COMVRF001-00MS
 * ASIS : addCert.js line 1229 ~ 1268 , MA3CMMAAT003_101S
 */
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalErrorStore, useThrottle } from 'scbk-services';
import { Dialog } from 'scbk-shared-module';
import { BottomButtons, ConsentRow, FieldWrapper, InfoText } from 'scbk-ui-web';
import { useAutVrfAddConfirmOverseasDeparture } from '../hooks/api/autVrfAdditionalHooks';
import { useAdditionalStore } from '../stores/useAdditionalStore';

interface Props {
  onComplete?: () => void;
  isLoading?: boolean;
}

const AdditionalOverseasDepartureComponent: React.FC<Props> = ({
  onComplete,
  isLoading = false,
}) => {
  const [confirm, setConfirm] = useState(false);

  const consentRowRef = useRef<HTMLButtonElement>(null);

  // 다국어 처리
  const { t } = useTranslation();

  const { activateInfo } = useAdditionalStore();

  const { request: confirmOverseasDeparture } = useAutVrfAddConfirmOverseasDeparture({
    onError: (error) => {
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const throttleClick = useThrottle(() => {
    execConfirmOverseasDeparture();
  }, 1000);

  const execConfirmOverseasDeparture = useCallback(async () => {
    console.log(confirm);
    if (confirm === false) {
      const isOK = await Dialog.alert({
        content: t('AdditionalComponent.101', '내용을 확인해 주세요.'),
      });

      if (isOK) {
        consentRowRef.current?.focus();
      }

      return;
    }

    const result = await confirmOverseasDeparture({
      authType: activateInfo?.additionalType,
      transType: activateInfo?.transType,
    });

    const resultCode = result?.resultCode;
    const abroadYn = result?.abroadYn;

    if (resultCode == '000' || resultCode == '0') {
      if (abroadYn == '00' || abroadYn == '0') {
        Dialog.alert({
          content: t(
            'AdditionalComponent.102',
            '법무부 출입국 조회서비스를 통해 해외체류사실이 확인 되었습니다.',
          ),
          onClick: () => {
            onComplete?.();
          },
        });
      } else if (abroadYn == '01' || abroadYn == '1') {
        Dialog.alert({
          title: t('AdditionalComponent.105', '오류'),
          content: t(
            'AdditionalComponent.103',
            '법무부 출입국 조회서비스를 통해 해외 출국사실이 없습니다. 다른 추가인증 수단을 이용하시기 바랍니다.',
          ),
          onClick: () => {
            setConfirm(false);
          },
        });
      } else {
        Dialog.alert({
          title: t('AdditionalComponent.105', '오류'),
          content: t(
            'AdditionalComponent.104',
            '법무부 출입국 조회서비스 접속량이 많습니다. 다시 시도하여 주시기 바랍니다.',
          ),
          onClick: () => {
            setConfirm(false);
          },
        });
      }
    } else {
      Dialog.alert({
        title: t('AdditionalComponent.105', '오류'),
        content: t(
          'AdditionalComponent.104',
          '법무부 출입국 조회서비스 접속량이 많습니다. 다시 시도하여 주시기 바랍니다.',
        ),
        onClick: () => {
          setConfirm(false);
        },
      });
    }
  }, [confirm]);

  return (
    <>
      <h2 className="page-heading-title mb-8">
        {t('AdditionalComponent.047', '해외체류인증을 진행할게요')}
      </h2>
      <p className="page-desc-lg">
        {t('AdditionalComponent.048', '한국국적의 해외체류자, 해외거주자만 이용할 수 있어요.')}
      </p>

      <div className="flex flex-col gap-2 mt-12">
        <h3 className="area-title">{t('AdditionalComponent.049', '출국사실 확인 동의')}</h3>
        <p className="body-lg text-basic-04 font-weight-medium">
          {t(
            'AdditionalComponent.050',
            '출국사실을 확인하기 위하여 개인정보보호법 제15조(개인정보의 수집/이용) 및 제18조(개인정보의 이용/제공 제한)에 따라 고객님의 주민등록번호 및 성명 등을 출국정보 조회시스템을 운영하고 있는 금융결제원을 통하여 법무부 출입국관리사무소로 전송하고, 확인 받는데 동의하십니까?',
          )}
        </p>
      </div>
      <div className="flex flex-col gap-3 mt-8">
        <InfoText
          type="primary"
          text={t(
            'AdditionalComponent.051',
            '고객님께서는 동의를 거부하실 수 있으며, 동의를 거부하실 경우에는 서비스 이용에 제한이 있을 수 있습니다.',
          )}
        />
        <InfoText
          type="primary"
          text={t(
            'AdditionalComponent.052',
            '고객님의 출국정보는 별도의 추가 본인인증 절차를 생략하기 위한 목적에 한하여 사용합니다.',
          )}
        />
        <InfoText
          type="primary"
          text={t(
            'AdditionalComponent.053',
            '해외 IP임이 확인된 경우에만 이용이 가능하며, 법무부 출입국관리사무소 시스템 장애 및 출입국정보 업데이트가 지연될 경우 출국정보 이용이 제한될 수 있습니다.(출국정보 업데이트 주기: 출입국 후 최소 12시간~36시간)',
          )}
        />
      </div>

      <ConsentRow
        buttonRef={consentRowRef}
        single
        variants="notice"
        action="confirm"
        active={confirm} //확인버튼 활성화여부
        onClick={() => {
          setConfirm(!confirm);
        }}
        btnText={t('AdditionalComponent.127', '확인')}
      >
        <FieldWrapper>
          <span className="body-lg text-basic-04">
            {t('AdditionalComponent.054', '위 출국사실 확인 동의 내용을 확인했어요')}
          </span>
        </FieldWrapper>
      </ConsentRow>

      <BottomButtons
        buttons={[
          {
            label: t('AdditionalComponent.055', '다음'),
            onClick: () => {
              throttleClick();
            },
            Variants: 'primary',
            disabled: isLoading,
          },
        ]}
      />
    </>
  );
};

export default AdditionalOverseasDepartureComponent;
